import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./hero-carousel-style.scss";
import videoUrl from "../../../utils/video-url";

//首頁主視覺輪播（無限循環 + 中間放大）
//
//  為什麼不用 scroll-snap：
//    捲動式輪播做不到「無縫循環」—— 捲到最後一張要回第一張時，
//    瀏覽器一定會把整條捲軸拉回去，畫面會倒退一大段。
//  改用 transform 位移 + 三份複製的做法：
//    items = [原陣列, 原陣列, 原陣列]，index 從「中間那份」開始。
//    往前往後走都還有一整份可以走，走到邊界時在動畫結束的瞬間
//    把 index 平移一份、同時關掉 transition —— 視覺上完全看不出來。
//
//  中間放大：非中間的卡片縮小並降低透明度，
//  比「把中間放大」更好，因為版面高度不會因為放大而跳動。
//刻意不做自動輪播：換一張要由使用者的手指決定。
//  自動播的話，人正在讀某一張卡的說明時它會自己走掉 ——
//  而且首頁的卡片是要被「挑」的，不是跑馬燈。
const SWIPE_THRESHOLD = 40; //滑動超過這個距離才換頁，避免誤觸

//—— 預載產品頁的開場影片 ——
//
//  產品頁一進去就是滿版影片（六、七 MB）。在行動網路上要等它載到夠才動得起來，
//  在那之前畫面是一張靜止的第一格 —— 看起來像卡住了。
//  趁使用者還在首頁看卡片時先把它抓下來，點進去時已經在快取裡。
//
//  用 fetch 而不是 <link rel="prefetch"> 或隱藏的 <video preload="auto">：
//    rel=prefetch      Safari 不支援，而我們要救的正是 iPhone。
//    preload="auto"    iOS 在行動網路下會自己降成 none，等於沒預載。
//    fetch             就是一般請求，兩邊都吃。抓完把 body 丟掉，只留下快取。
//
//  要等它「真的停在中間」才抓（DWELL_MS）：手指一路滑過去時會掃過好幾張，
//  一掃過就抓的話，等於替使用者決定要花掉幾十 MB 的流量。
const PREFETCH_DWELL_MS = 1600;

const HeroCarousel = ({ slides }) => {
  const n = slides.length;
  //三份複製：前後各留一份當緩衝
  const items = [...slides, ...slides, ...slides];

  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const touchStartX = useRef(null);
  //已經預載過的影片，避免同一支被重複抓
  const prefetched = useRef(new Set());

  const [index, setIndex] = useState(n); //從中間那份的第一張開始
  const [animate, setAnimate] = useState(true);
  const [metrics, setMetrics] = useState({ slideW: 0, gap: 0, viewW: 0 });

  //量測卡片寬度與間距
  //  寬度由 CSS 的百分比決定（不同斷點不同），所以要真的量而不是寫死。
  //
  //  量的是 .hero__slide（外層），它刻意不帶 transform —— 縮放套在內層的
  //  .hero__card 上。若外層有 scale，這裡量到的會是縮小後的寬度，
  //  位移每張就少算一點、越走越偏。
  //  用 getBoundingClientRect() 而非 offsetWidth 是為了拿到小數寬度，
  //  offsetWidth 會取整，累積下來仍有幾 px 誤差。
  const measure = useCallback(() => {
    const vp = viewportRef.current;
    const track = trackRef.current;
    const first = track?.children?.[0];
    if (!vp || !track || !first) return;

    setMetrics({
      slideW: first.getBoundingClientRect().width,
      gap: parseFloat(getComputedStyle(track).gap) || 0,
      viewW: vp.clientWidth,
    });
  }, []);

  useEffect(() => {
    measure();
    const vp = viewportRef.current;
    if (!vp) return;
    //視窗縮放會改變卡片寬度（百分比），要重算位移量
    const ro = new ResizeObserver(measure);
    ro.observe(vp);
    return () => ro.disconnect();
  }, [measure]);

  //把第 index 張推到正中間
  const offset =
    (metrics.viewW - metrics.slideW) / 2 -
    index * (metrics.slideW + metrics.gap);

  //走到緩衝區邊界 → 無動畫平移回中間那份
  //
  //  一定要確認事件來源是軌道本身：transitionend 會冒泡，而每張 .hero__card
  //  自己也有過場（transform 0.5s、opacity 0.4s）。卡片的 opacity 比軌道的位移
  //  早 100ms 結束，那顆冒泡上來的事件會讓平移在軌道還在滑的時候就執行，
  //  位置被瞬間抽換 —— 畫面就是「換下一張時跳一下」。
  const handleTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;

    if (index >= n * 2) {
      setAnimate(false);
      setIndex(index - n);
    } else if (index < n) {
      setAnimate(false);
      setIndex(index + n);
    }
  };

  //平移完成後把動畫開回來
  //  要等兩幀：第一幀套用新的 index（無動畫），第二幀才恢復 transition，
  //  只等一幀的話瀏覽器可能把兩次變更合併，平移就會被看見。
  useEffect(() => {
    if (animate) return;
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setAnimate(true)),
    );
    return () => cancelAnimationFrame(id);
  }, [animate]);

  const go = useCallback((step) => setIndex((i) => i + step), []);

  //觸控滑動：transform 式輪播沒有原生捲動，手勢要自己接
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start == null) return;

    const dx = e.changedTouches[0].clientX - start;
    if (Math.abs(dx) > SWIPE_THRESHOLD) go(dx < 0 ? 1 : -1);
  };

  //目前實際是第幾張（換算回原陣列）
  const activeDot = ((index % n) + n) % n;

  //停在中間夠久就預載那一張的開場影片。
  //  一支只抓一次（prefetched），而且使用者開了資料節省模式或連線很慢就不抓 ——
  //  那種情況下預載只會拖慢他真正想看的東西。
  //  （navigator.connection 在 Safari 上是 undefined，所以那兩個判斷在 iPhone 上不會成立，
  //    這是可接受的：iPhone 正是最需要這個預載的裝置。）
  useEffect(() => {
    const url = slides[activeDot]?.intro;
    if (!url || prefetched.current.has(url)) return;

    const net = navigator.connection;
    if (net?.saveData) return;
    if (net?.effectiveType && /(^|-)2g$/.test(net.effectiveType)) return;

    const timer = setTimeout(() => {
      prefetched.current.add(url);
      //body 要讀掉才會進快取，讀完就讓它被回收 —— 我們要的是快取，不是這份資料
      //  網址一定要走 videoUrl（跟產品頁同一支）：少了版本參數就是另一個
      //  快取項目，預載到的那一份不會被用到。
      fetch(videoUrl(url))
        .then((res) => res.blob())
        .catch(() => {});
    }, PREFETCH_DWELL_MS);

    return () => clearTimeout(timer);
  }, [activeDot, slides]);

  return (
    <section
      className="hero"
      aria-roledescription="輪播"
      aria-label="精選命理服務"
    >
      <div
        className="hero__viewport"
        ref={viewportRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="hero__track"
          ref={trackRef}
          style={{
            transform: `translate3d(${offset}px, 0, 0)`,
            transition: animate
              ? "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)"
              : "none",
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {items.map((slide, i) => {
            const isCenter = i === index;
            return (
              <div
                className={isCenter ? "hero__slide is-center" : "hero__slide"}
                key={`${slide.id}-${i}`}
                //非中間的卡片不該被 Tab 選到，也不該被讀屏念出來
                aria-hidden={!isCenter}
              >
                {/* 有美術圖就用圖，沒有才退回漸層大字 ——
                    和 ProductCard 同一套判斷，補圖時不用記得還有第二個地方要改 */}
                <Link
                  to={slide.path || "#"}
                  className={`hero__card hero__card--${slide.theme}`}
                  tabIndex={isCenter ? 0 : -1}
                  style={
                    slide.image
                      ? { backgroundImage: `url(${slide.image})` }
                      : undefined
                  }
                >
                  {!slide.image && (
                    <span className="hero__glyph">{slide.glyph}</span>
                  )}

                  <div className="hero__info">
                    {slide.tag && (
                      <span className="hero__tag">{slide.tag}</span>
                    )}
                    <h2 className="hero__title">{slide.title}</h2>
                    <p className="hero__subtitle">{slide.subtitle}</p>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      <button
        type="button"
        className="hero__arrow hero__arrow--prev"
        aria-label="上一張"
        onClick={() => go(-1)}
      >
        <ArrowIcon dir="left" />
      </button>

      <button
        type="button"
        className="hero__arrow hero__arrow--next"
        aria-label="下一張"
        onClick={() => go(1)}
      >
        <ArrowIcon dir="right" />
      </button>

      {/* 計數器：手機寬度有限，「4 / 10」比一整排圓點省位置，
          張數多的時候圓點也會擠成一團看不清楚 */}
      <div className="hero__counter" aria-live="polite">
        {activeDot + 1} / {n}
      </div>
    </section>
  );
};

const ArrowIcon = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <path
      d={dir === "left" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"}
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default HeroCarousel;
